import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '../context/AuthContext';
import { getPublicSites, getPublicUnits } from '../services/siteService';
import { getRegistrationRoles } from '../services/authService';
import Button from '../components/common/Button';
import { alphabeticalSort, naturalSort } from '../utils/sortUtils';

// Validation schema
const RegisterSchema = Yup.object().shape({
  firstName: Yup.string()
    .required('First name is required')
    .min(2, 'First name must be at least 2 characters'),
  lastName: Yup.string()
    .required('Last name is required')
    .min(2, 'Last name must be at least 2 characters'),
  email: Yup.string()
    .email('Invalid email address')
    .required('Email is required'),
  password: Yup.string()
    .required('Password is required')
    .min(6, 'Password must be at least 6 characters'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password'), null], 'Passwords must match')
    .required('Confirm password is required'),
  cellphone: Yup.string()
    .required('Mobile number is required')
    .matches(/^[0-9+\s-]+$/, 'Invalid mobile number format'),
  siteUuid: Yup.string()
    .required('Site selection is required'),
  siteName: Yup.string()
    .required('Site name is required'),
  unitId: Yup.string()
    .required('Unit selection is required'),
  selectedRole: Yup.string()
    .required('Role selection is required'),
});

function Register() {
  const { register, loading } = useAuth();
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [sites, setSites] = useState([]);
  const [units, setUnits] = useState([]);
  const [roles, setRoles] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [sitesLoading, setSitesLoading] = useState(false);
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [rolesLoading, setRolesLoading] = useState(false);

  // Fetch sites on component mount
  useEffect(() => {
    const fetchSites = async () => {
      try {
        setSitesLoading(true);
        const sitesData = await getPublicSites();
        
        // Debug the structure to see what we're working with
        // console.log('Site data structure:', sitesData[0]);
        
        // TODO: Restore Lab filter for production
        // const filteredSites = alphabeticalSort(
        //   sitesData.filter(site => site.name !== 'Lab'), 
        //   'name'
        // );
        const filteredSites = alphabeticalSort(sitesData, 'name');
        
        setSites(filteredSites);
      } catch (err) {
        // console.error('Failed to fetch sites:', err);
        setError('Failed to load sites. Please try again.');
      } finally {
        setSitesLoading(false);
      }
    };

    fetchSites();
  }, []);

  // Fetch roles on component mount
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        setRolesLoading(true);
        const rolesData = await getRegistrationRoles();
        
        // Sort roles alphabetically by display name using our utility
        const sortedRoles = alphabeticalSort(rolesData, 'display_name');
        
        setRoles(sortedRoles);
      } catch (err) {
        // console.error('Failed to fetch registration roles:', err);
        setError('Failed to load roles. Please try again.');
      } finally {
        setRolesLoading(false);
      }
    };

    fetchRoles();
  }, []);

  // Fetch units when a site is selected
  const handleSiteChange = async (e, setFieldValue) => {
    const siteUuid = e.target.value;
    setFieldValue('siteUuid', siteUuid);
    setFieldValue('unitId', ''); // Reset unit selection
    
    if (!siteUuid) {
      setSelectedSite(null);
      setUnits([]);
      return;
    }

    try {
      setUnitsLoading(true);
      const site = sites.find(site => site.uuid === siteUuid);
      setSelectedSite(site);
      
      // Set the siteName field when a site is selected
      setFieldValue('siteName', site.name);
      
      const unitsData = await getPublicUnits(siteUuid);
      
      // Sort units using natural sort (properly handles numeric values)
      const sortedUnits = naturalSort(unitsData, 'displayName');
      
      setUnits(sortedUnits);
    } catch (err) {
      // console.error('Failed to fetch units:', err);
      setError('Failed to load units. Please try again.');
      setUnits([]);
    } finally {
      setUnitsLoading(false);
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setError(null);
      // Remove confirmPassword before sending to API
      const { confirmPassword, ...userData } = values;
      
      // Include the unit display name for reference
      const selectedUnit = units.find(unit => unit.unitId.toString() === values.unitId.toString());
      const registrationData = {
        ...userData,
        unitDisplayName: selectedUnit?.displayName || ''
      };
      
      const response = await register(registrationData);
      
      // Redirect to the pending approval page instead of dashboard
      navigate('/pending-approval', { 
        state: { 
          userInfo: response.user || {
            ...registrationData,
            siteName: selectedSite?.name || "Pending verification"
          }
        }
      });
    } catch (err) {
      // Use userMessage if available, as it contains our formatted validation messages
      setError(err.response?.data?.userMessage || err.response?.data?.message || 'Registration failed. Please try again.');
      // Scroll to top of page to ensure error message is visible
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-md mx-auto mt-8 mb-12">
      <h1 className="text-3xl font-bold text-center mb-6 text-hooray-red font-mont">Create an Account</h1>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4" 
             style={{ whiteSpace: 'pre-line' }}>
          {error}
        </div>
      )}
      
      <div className="bg-white rounded-lg shadow-md p-6">
        <Formik
          initialValues={{ 
            firstName: '',
            lastName: '', 
            email: '', 
            password: '', 
            confirmPassword: '',
            cellphone: '',
            siteUuid: '',
            siteName: '',
            unitId: '',
            selectedRole: ''
          }}
          validationSchema={RegisterSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, touched, errors, setFieldValue, values }) => (
            <Form>
              <div className="mb-4">
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                  First Name
                </label>
                <Field
                  type="text"
                  name="firstName"
                  id="firstName"
                  className={`w-full px-3 py-2 border rounded-md ${
                    touched.firstName && errors.firstName ? 'border-red-500' : 'border-gray-300'
                  } focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent`}
                />
                <ErrorMessage name="firstName" component="div" className="text-red-500 text-sm mt-1" />
              </div>
              
              <div className="mb-4">
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name
                </label>
                <Field
                  type="text"
                  name="lastName"
                  id="lastName"
                  className={`w-full px-3 py-2 border rounded-md ${
                    touched.lastName && errors.lastName ? 'border-red-500' : 'border-gray-300'
                  } focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent`}
                />
                <ErrorMessage name="lastName" component="div" className="text-red-500 text-sm mt-1" />
              </div>
              
              <div className="mb-4">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <Field
                  type="email"
                  name="email"
                  id="email"
                  className={`w-full px-3 py-2 border rounded-md ${
                    touched.email && errors.email ? 'border-red-500' : 'border-gray-300'
                  } focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent`}
                />
                <ErrorMessage name="email" component="div" className="text-red-500 text-sm mt-1" />
              </div>
              
              <div className="mb-4">
                <label htmlFor="cellphone" className="block text-sm font-medium text-gray-700 mb-1">
                  Mobile Number
                </label>
                <Field
                  type="text"
                  name="cellphone"
                  id="cellphone"
                  placeholder="e.g. 082 456 7890"
                  className={`w-full px-3 py-2 border rounded-md ${
                    touched.cellphone && errors.cellphone ? 'border-red-500' : 'border-gray-300'
                  } focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent`}
                />
                <ErrorMessage name="cellphone" component="div" className="text-red-500 text-sm mt-1" />
              </div>
              
              <div className="mb-4">
                <label htmlFor="siteUuid" className="block text-sm font-medium text-gray-700 mb-1">
                  Site
                </label>
                <Field
                  as="select"
                  name="siteUuid"
                  id="siteUuid"
                  onChange={(e) => handleSiteChange(e, setFieldValue)}
                  disabled={sitesLoading}
                  className={`w-full px-3 py-2 border rounded-md ${
                    touched.siteUuid && errors.siteUuid ? 'border-red-500' : 'border-gray-300'
                  } focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                    sitesLoading ? 'bg-gray-100' : ''
                  }`}
                >
                  <option value="">Select a site</option>
                  {sites.map(site => (
                    <option key={site.uuid} value={site.uuid}>
                      {site.name}
                    </option>
                  ))}
                </Field>
                {sitesLoading && (
                  <div className="text-sm text-gray-500 mt-1">Loading sites...</div>
                )}
                <ErrorMessage name="siteUuid" component="div" className="text-red-500 text-sm mt-1" />
              </div>
              
              {/* Hidden field for siteName */}
              <Field type="hidden" name="siteName" />
              
              <div className="mb-4">
                <label htmlFor="unitId" className="block text-sm font-medium text-gray-700 mb-1">
                  Unit
                </label>
                <Field
                  as="select"
                  name="unitId"
                  id="unitId"
                  disabled={!values.siteUuid || unitsLoading}
                  className={`w-full px-3 py-2 border rounded-md ${
                    touched.unitId && errors.unitId && values.siteUuid ? 'border-red-500' : 'border-gray-300'
                  } focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                    !values.siteUuid || unitsLoading ? 'bg-gray-100' : ''
                  }`}
                >
                  <option value="">Select a unit</option>
                  {units.map(unit => (
                    <option key={unit.unitId} value={unit.unitId}>
                      {unit.displayName}
                    </option>
                  ))}
                </Field>
                {unitsLoading && (
                  <div className="text-sm text-gray-500 mt-1">Loading units...</div>
                )}
                {!values.siteUuid && !unitsLoading && (
                  <div className="text-sm text-gray-500 mt-1">Select a site first</div>
                )}
                {touched.unitId && errors.unitId && values.siteUuid && (
                  <div className="text-red-500 text-sm mt-1">{errors.unitId}</div>
                )}
              </div>
              
              <div className="mb-4">
                <label htmlFor="selectedRole" className="block text-sm font-medium text-gray-700 mb-1">
                  Your Role
                </label>
                <Field
                  as="select"
                  name="selectedRole"
                  id="selectedRole"
                  disabled={!values.unitId || rolesLoading}
                  className={`w-full px-3 py-2 border rounded-md ${
                    touched.selectedRole && errors.selectedRole && values.unitId ? 'border-red-500' : 'border-gray-300'
                  } focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                    !values.unitId || rolesLoading ? 'bg-gray-100' : ''
                  }`}
                >
                  <option value="">Select your role</option>
                  {roles.map(role => (
                    <option key={role.value} value={role.value}>
                      {role.display_name}
                    </option>
                  ))}
                </Field>
                {rolesLoading && (
                  <div className="text-sm text-gray-500 mt-1">Loading roles...</div>
                )}
                {!values.unitId && !rolesLoading && (
                  <div className="text-sm text-gray-500 mt-1">Select a unit first</div>
                )}
                {touched.selectedRole && errors.selectedRole && values.unitId && (
                  <div className="text-red-500 text-sm mt-1">{errors.selectedRole}</div>
                )}
              </div>
              
              <div className="mb-4">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <Field
                  type="password"
                  name="password"
                  id="password"
                  className={`w-full px-3 py-2 border rounded-md ${
                    touched.password && errors.password ? 'border-red-500' : 'border-gray-300'
                  } focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent`}
                />
                <ErrorMessage name="password" component="div" className="text-red-500 text-sm mt-1" />
              </div>
              
              <div className="mb-6">
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm Password
                </label>
                <Field
                  type="password"
                  name="confirmPassword"
                  id="confirmPassword"
                  className={`w-full px-3 py-2 border rounded-md ${
                    touched.confirmPassword && errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                  } focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent`}
                />
                <ErrorMessage name="confirmPassword" component="div" className="text-red-500 text-sm mt-1" />
              </div>
              
              <Button
                type="submit"
                variant="cta"
                className="w-full"
                disabled={isSubmitting || loading}
              >
                {isSubmitting || loading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating account...
                  </span>
                ) : (
                  'Register'
                )}
              </Button>
            </Form>
          )}
        </Formik>
        
        <div className="mt-4 text-center">
          <p className="text-sm text-gray-600">
            Already have an account?{' '}
            <Link to="/login" className="text-hooray-red hover:text-hooray-yellow font-medium">
              Login here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Register; 